/**
 * ProjectBoardCard - one project as a draggable tile on the stage board.
 *
 * Scanned, not read - but it still has to answer the questions a board asks at
 * a glance (UI feedback P-2): what it is, how it stands (status + priority),
 * what it is about (a one-line description), what it carries and when it is due,
 * and who is on it. Each lives in a fixed slot so a column of cards lines up row
 * for row rather than jostling by how much metadata each happens to have.
 * Everything deeper is one click away on the project itself.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiCalendarBlankLight, PiLinkSimpleLight, PiUsersThreeLight } from 'react-icons/pi';
import GlassCard from '../shared/brand/GlassCard';
import Badge from '../shared/TailwindComponents/Badge';
import ProfileImage from '../Header/ProfileImage';
import { formatDate } from '../../utils/functions';
import { dueTone as dueToneFor, dueCountdown, priorityColorMap, statusColorMap, titleCase } from './projectFacts';

interface Props {
  project: any;
  onClick: (uuid: string) => void;
}

const initialsOf = (name: string): string =>
  name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() || '').join('');

const ProjectBoardCard: React.FC<Props> = ({ project, onClick }) => {
  const { t } = useTranslation();
  const dueTone = dueToneFor(project.due_date);
  const countdown = dueCountdown(project.due_date);

  const collaborators: any[] = Array.isArray(project.shared_with) ? project.shared_with : [];
  const extraCollaborators = Math.max(0, collaborators.length - (project.owner?.name ? 1 : 0));

  return (
    <GlassCard
      padding="sm"
      className="cursor-pointer transition-shadow hover:shadow-md"
    >
      <div onClick={() => onClick(project.uuid)}>
        {/* identity + priority */}
        <div className="flex items-start gap-2">
          <p className="min-w-0 flex-1 text-sm font-medium text-fg-strong line-clamp-2">
            {project.title || t('Untitled')}
          </p>
          {project.priority && (
            <Badge color={priorityColorMap[project.priority] || 'gray'} className="flex-shrink-0 capitalize">
              {titleCase(project.priority)}
            </Badge>
          )}
        </div>

        {/* what it is about - one quiet line, only when there is one */}
        {project.description && (
          <p className="mt-1 text-[11px] text-fg-muted line-clamp-1">{project.description}</p>
        )}

        {/* how it stands + what it carries */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Badge color={statusColorMap[project.status] || 'gray'}>
            {project.status ? titleCase(project.status) : t('Active')}
          </Badge>
          {project.resource_count > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-fg-muted">
              <PiLinkSimpleLight className="h-3 w-3" />
              {project.resource_count}
            </span>
          )}
        </div>

        {/* when + who, on one aligned footer rail */}
        <div className="mt-2 flex items-center justify-between gap-2">
          {project.due_date ? (
            <span
              className={clsx(
                'inline-flex min-w-0 items-center gap-1 text-[11px]',
                dueTone === 'danger'
                  ? 'text-danger-600 dark:text-danger-400'
                  : dueTone === 'warning'
                    ? 'text-warning-600 dark:text-warning-400'
                    : 'text-fg-muted'
              )}
            >
              <PiCalendarBlankLight className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{countdown || formatDate(project.due_date)}</span>
            </span>
          ) : <span className="text-[11px] text-fg-muted/70">{t('No due date')}</span>}

          <div className="flex flex-shrink-0 items-center gap-1">
            {project.owner?.name && (
              <ProfileImage
                profilePicture={project.owner.profile_picture || null}
                email={project.owner.email || null}
                initials={initialsOf(project.owner.name)}
                name={project.owner.name}
                size="w-5 h-5"
              />
            )}
            {extraCollaborators > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-fg-muted">
                <PiUsersThreeLight className="h-3 w-3" />+{extraCollaborators}
              </span>
            )}
          </div>
        </div>
      </div>
    </GlassCard>
  );
};

export default ProjectBoardCard;
